import React from 'react';
import { MdArrowBack, MdChevronRight } from 'react-icons/md';
import { useProfile } from '../contexts/ProfileContext';
import { useApp } from '../contexts/AppContext';
import accountsData from '../data/accounts.json';

const bankingData = accountsData.bankingData;
const bankingTotals = accountsData.bankingTotals;
const investingData = accountsData.investingData;
const investingTotals = accountsData.investingTotals;
const creditDataSummary = accountsData.creditDataSummary;

const AccountListItem = ({ name, num, balance, currency, onClick, isDebt }) => (
  <div className="account-list-item" onClick={onClick}>
    <div className="ali-left">
      <div className="ali-name">{name}</div>
      <div className="ali-num">{num}</div>
    </div>
    <div className="ali-right">
      {currency === 'USD' && <span className="ali-currency">USD</span>}
      <div className={`ali-balance ${isDebt ? '' : 'positive-green'}`}>{balance}</div>
      <MdChevronRight className="ali-arrow" style={{opacity: 0.3}} />
    </div>
  </div>
);

// Normalize columnar {cols, rows} or array-of-objects transaction format
function normTxns(transactions) {
  if (!transactions) return [];
  if (Array.isArray(transactions)) return transactions;
  if (transactions.cols && Array.isArray(transactions.rows)) {
    const { cols, rows } = transactions;
    return rows.map(row => Object.fromEntries(cols.map((c, i) => [c, row[i]])));
  }
  return [];
}

// Roll back an account balance to what it was on simDate.
// Checking/savings: use balance_after from the last transaction ≤ simDate.
// Credit cards (current_balance_owing): reverse from end by adding future amounts
//   (positive = payment reduces owing, negative = purchase increases owing).
// Investments (current_value/current_market_value): not rolled back — market-driven.
function balanceAt(acct, simDate) {
  const raw = acct.current_balance ?? acct.current_balance_owing ?? acct.current_value ?? acct.current_market_value ?? acct.balance ?? 0;
  if (!simDate || !acct.transactions) return raw;

  const isInvestment = acct.current_value !== undefined || acct.current_market_value !== undefined;
  if (isInvestment) return raw;

  const txns = normTxns(acct.transactions);
  if (!txns.length) return raw;

  // Checking/savings: balance_after column matches current_balance exactly
  if ('balance_after' in txns[0] && Math.abs((txns[txns.length - 1].balance_after ?? NaN) - raw) < 1) {
    const past = txns.filter(t => t.date && t.date <= simDate);
    return past.length ? past[past.length - 1].balance_after : 0;
  }

  // Credit cards: amounts are payment-positive / purchase-negative relative to owing
  if (acct.current_balance_owing !== undefined) {
    const futureSum = txns.filter(t => t.date && t.date > simDate).reduce((s, t) => s + (t.amount ?? 0), 0);
    return raw + futureSum;
  }

  return raw;
}

function profileAccountsToDisplay(accounts, simDate) {
  if (!accounts) return { banking: [], investing: [], credit: [] };
  const fmt = v => `$${Number(Math.abs(v || 0)).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const last4 = s => s ? `...${String(s).slice(-4)}` : '...xxxx';
  const banking = [], investing = [], credit = [];

  const CREDIT_KEY  = /credit|mortgage|_loc$|loan/i;
  const INVEST_KEY  = /tfsa|rrsp|rrif|resp|invest|pension/i;
  const CREDIT_TYPE = /credit|mortgage|line of credit|loan|visa|mastercard|amex/i;
  const INVEST_TYPE = /invest|tfsa|rrsp|rrif|resp|savings plan|non-registered/i;

  for (const [key, acct] of Object.entries(accounts)) {
    if (!acct || typeof acct !== 'object') continue;
    const bal  = balanceAt(acct, simDate);
    const name = acct.account_type || acct.name || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const num  = last4(acct.account_number || acct.card_number);
    const entry = { id: key, name, num, balance: fmt(bal), currency: acct.currency || 'CAD' };

    const typeStr = acct.account_type || '';
    let bucket;
    if      (CREDIT_KEY.test(key) || CREDIT_TYPE.test(typeStr)) bucket = 'credit';
    else if (INVEST_KEY.test(key) || INVEST_TYPE.test(typeStr)) bucket = 'investing';
    else                                                         bucket = 'banking';

    if      (bucket === 'credit')   credit.push({ ...entry, isDebt: true });
    else if (bucket === 'investing') investing.push(entry);
    else                             banking.push(entry);
  }

  return { banking, investing, credit };
}

const AccountsScreen = ({ onNavigate, onBack }) => {
  const { loadedProfileData } = useProfile();
  const { simDate } = useApp();
  const profile = loadedProfileData?.profile;
  const profileAccounts = profile?.accounts ? profileAccountsToDisplay(profile.accounts, simDate) : null;

  const showBanking  = profileAccounts ? profileAccounts.banking  : bankingData;
  const showInvest   = profileAccounts ? profileAccounts.investing : investingData;
  const showCredit   = profileAccounts ? profileAccounts.credit    : null;

  const bankTotal    = profileAccounts
    ? profileAccounts.banking.reduce((s, a) => s + Math.abs(parseFloat((a.balance || '0').replace(/[$,]/g, ''))), 0)
    : null;
  const investTotal  = profileAccounts
    ? profileAccounts.investing.reduce((s, a) => s + Math.abs(parseFloat((a.balance || '0').replace(/[$,]/g, ''))), 0)
    : null;

  return (
    <div className="screen accounts-screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate('home'))} aria-label="Back to home">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">Accounts</h1>
        </div>
      </header>

      <div className="scroll-content gray-bg">

        <div className="list-group-title">BANKING</div>
        <div className="white-container">
          {showBanking.map(acc => (
            <AccountListItem key={acc.id} {...acc} onClick={() => onNavigate('accountDetails', acc.id)} />
          ))}
          <div className="account-total-row">
            <span>Total</span>
            <div className="total-values">
              <div className="positive-green">
                {bankTotal !== null ? `$${bankTotal.toLocaleString('en-CA', {minimumFractionDigits:2})}` : bankingTotals.cad}
              </div>
              {!profileAccounts && <div className="usd-subtotal"><small>USD</small> {bankingTotals.usd}</div>}
            </div>
          </div>
        </div>

        {showInvest.length > 0 && (
          <>
            <div className="list-group-title">INVESTING</div>
            <div className="white-container">
              {showInvest.map(acc => (
                <AccountListItem key={acc.id} {...acc} onClick={() => onNavigate('accountDetails', acc.id)} />
              ))}
              <div className="account-total-row">
                <span>Total</span>
                <div className="total-values">
                  <div className="positive-green">
                    {investTotal !== null ? `$${investTotal.toLocaleString('en-CA', {minimumFractionDigits:2})}` : investingTotals.cad}
                  </div>
                  {!profileAccounts && <div className="usd-subtotal"><small>USD</small> {investingTotals.usd}</div>}
                </div>
              </div>
            </div>
          </>
        )}

        <div className="list-group-title">CREDIT</div>
        <div className="white-container">
          {profileAccounts && showCredit.length > 0
            ? showCredit.map(acc => <AccountListItem key={acc.id} {...acc} onClick={() => onNavigate('accountDetails', acc.id)} />)
            : <>
                <AccountListItem {...creditDataSummary[0]} isDebt onClick={() => onNavigate('credit')} />
                <AccountListItem {...creditDataSummary[1]} isDebt onClick={() => {}}/>
              </>
          }
        </div>

        <div className="footer-disclaimer">
            <p>Investment Products offered by:</p>
            <a href="https://www.td.com/ca/en/investing">TD Wealth</a>
            <a href="https://www.td.com/ca/en/about-td/customer-care/legal-notices-and-disclaimers">Legal & Disclaimer</a>
        </div>
      </div>
    </div>
  );
};

export default AccountsScreen;
